import { useEffect, useMemo, useRef, useState } from 'react';
import { dateKey } from '../../lib/dateKey';
import { debounce } from '../../lib/debounce';
import { loadDailyProgress, saveDailyProgress } from '../../lib/storage/indexedDb';
import { runOnKey } from '../../lib/storage/keyedQueue';
import type { DailyProgress, Id } from '../../lib/types';
import { padCompleted } from './padCompleted';
import { subscribeProgress } from './progressEvents';
import { syncEngine } from '../sync/engine';

// Per plan/02 §Write strategy: kids tap fast; debounce IDB writes to ~300 ms.
const FLUSH_DELAY_MS = 300;

// Per-slot LWW merge: for each index, pick `local` if the local timestamp is
// strictly newer than `otherTs`, else `other`. Extracted so the .map arrow
// doesn't sit four callback-levels deep inside the hook (S2004).
function mergeBySlotTs(
  local: boolean[],
  other: boolean[],
  slotTs: number[],
  otherTs: number,
): boolean[] {
  return local.map((v, i) => ((slotTs[i] ?? 0) > otherTs ? v : (other[i] ?? false)));
}

export interface RoutineProgress {
  completed: boolean[];
  hydrated: boolean;
  toggle: (index: number) => boolean[];
  clearAll: () => void;
}

// Tracks completion state for one (profile, routine, today). Hydrates from
// IDB on mount, exposes a `toggle` that mutates locally and schedules a
// debounced flush. The toggle returns the next completion array so the
// caller can react synchronously (e.g. navigate when all-done).
//
// Stale URL or absent profile is the caller's responsibility (TaskPage
// redirects). `today` is captured at mount; if the device crosses local
// midnight mid-session, the in-flight routine keeps its old dateKey until
// remount. Acceptable for v1 — kids don't open the app for hours.
export function useRoutineProgress(
  profileId: Id,
  routineId: Id,
  taskCount: number,
): RoutineProgress {
  const today = useMemo(() => dateKey(), []);
  const [completed, setCompleted] = useState<boolean[]>(() =>
    Array<boolean>(taskCount).fill(false),
  );
  const [hydrated, setHydrated] = useState(false);

  // Per-slot last-write timestamps (epoch ms). Set to Date.now() when the
  // user touches a slot; 0 means "never touched locally this session."
  // Kept in a ref because it doesn't drive renders — it only guards the
  // flush and subscription merge paths against stale-echo overwrites.
  const slotTsRef = useRef<number[]>(new Array<number>(taskCount).fill(0));

  useEffect(() => {
    let cancelled = false;
    // Reset slot timestamps whenever context changes (profile / routine /
    // day / taskCount). IDB is authoritative until the first user action.
    slotTsRef.current = new Array<number>(taskCount).fill(0);
    void loadDailyProgress(profileId, today).then((dp) => {
      if (cancelled) return;
      setCompleted(padCompleted(dp?.routines[routineId] ?? [], taskCount));
      setHydrated(true);
    });
    // Nudge the engine to converge — the task page is the most critical
    // moment for stale state since the user may have backgrounded the app.
    // No-op when the engine is stopped.
    void syncEngine.recover();
    return () => {
      cancelled = true;
    };
  }, [profileId, routineId, today, taskCount]);

  // The sync engine writes remote progress merges straight to IDB. This
  // subscription is the bridge that gets the new state into React without
  // a remount or re-read. Local taps don't emit (notifyProgress is silent),
  // so there's no echo loop — the optimistic path stays as-is.
  //
  // Per-slot LWW: if the user touched a slot after the remote's updatedAt,
  // keep the local value; otherwise take the remote. Prevents a stale echo
  // from undoing a local deselect while the hook is still in session.
  useEffect(() => {
    return subscribeProgress(profileId, today, (daily) => {
      const remote = padCompleted(daily.routines[routineId] ?? [], taskCount);
      const parsedTs = Date.parse(daily.updatedAt);
      const remoteTs = Number.isNaN(parsedTs) ? 0 : parsedTs;
      console.info('[progress:hook] applied', {
        routineId,
        completedLen: remote.length,
        remoteTs,
      });
      // Keep the local value only if the slot was touched after the remote
      // stamp; otherwise the remote wins.
      setCompleted((prev) => mergeBySlotTs(prev, remote, slotTsRef.current, remoteTs));
    });
  }, [profileId, routineId, today, taskCount]);

  const flush = useMemo(
    () =>
      debounce((next: boolean[]) => {
        // Serialize per (profile, day) so two routines flushing in quick
        // succession can't interleave their read-modify-write — including
        // the unmount-flush of one routine racing the mount-load of the next.
        void runOnKey(`${profileId}_${today}`, async () => {
          const current = await loadDailyProgress(profileId, today);
          // Per-slot LWW: take the local `next` value for slots the user
          // touched after IDB's updatedAt; yield to IDB for untouched slots.
          // Replaces the old isClear / OR-merge approach and correctly
          // handles single-slot deselects without clobbering other slots.
          const parsedTs = current ? Date.parse(current.updatedAt) : 0;
          const existingTs = Number.isNaN(parsedTs) ? 0 : parsedTs;
          const existing = current?.routines[routineId] ?? [];
          const merged = mergeBySlotTs(next, existing, slotTsRef.current, existingTs);
          const updated: DailyProgress = {
            profileId,
            dateKey: today,
            routines: { ...(current?.routines ?? {}), [routineId]: merged },
            updatedAt: new Date().toISOString(),
          };
          await saveDailyProgress(updated);
          // Hand off to the sync engine, which no-ops when stopped.
          await syncEngine.notifyProgress(updated, routineId);
        });
      }, FLUSH_DELAY_MS),
    [profileId, routineId, today],
  );

  // Capture the last tap if the user navigates away inside the debounce window.
  useEffect(
    () => () => {
      flush.flush();
    },
    [flush],
  );

  const toggle = (index: number): boolean[] => {
    slotTsRef.current[index] = Date.now();
    const next = [...completed];
    next[index] = !next[index];
    setCompleted(next);
    flush(next);
    return next;
  };

  const clearAll = (): void => {
    // Stamp with MAX_SAFE_INTEGER so the flush always wins against any IDB or
    // remote timestamp — an explicit clear must never be un-done by a stale echo.
    slotTsRef.current = slotTsRef.current.map(() => Number.MAX_SAFE_INTEGER);
    const allFalse = Array<boolean>(taskCount).fill(false);
    setCompleted(allFalse);
    flush(allFalse);
  };

  return { completed, hydrated, toggle, clearAll };
}
